import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, DownloadSimple } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function Gradebook() {
  const [grades, setGrades] = useState([]);
  const [exporting, setExporting] = useState(false);
  useEffect(() => { api.get("/grades").then((r) => setGrades(r.data)); }, []);

  const notes20 = grades.map((g) => (g.max ? (g.grade / g.max) * 20 : 0));
  const avg = notes20.length ? (notes20.reduce((a, b) => a + b, 0) / notes20.length).toFixed(2) : "0";

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get("/grades/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = "releve-de-notes.pdf";
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="gradebook-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Évaluations</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Carnet de notes</h1>
        </div>
        <Button variant="outline" onClick={exportPdf} disabled={exporting} data-testid="export-grades-pdf">
          <DownloadSimple size={16} className="mr-2" /> {exporting ? "Génération..." : "Exporter en PDF"}
        </Button>
      </div>

      <div className="flex items-center gap-4 rounded-xl border border-border bg-primary p-6 text-primary-foreground">
        <Trophy size={40} weight="duotone" />
        <div>
          <p className="text-sm opacity-90">Moyenne générale (sur 20)</p>
          <p className="font-display text-4xl font-extrabold">{avg}</p>
        </div>
      </div>

      {/* Vue carte (mobile) */}
      <div className="space-y-3 md:hidden">
        {grades.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">Aucune note pour l'instant.</p>
        ) : grades.map((g, i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <Badge variant="secondary" className="mb-1">{g.type}</Badge>
                <p className="truncate font-medium">{g.title}</p>
                <p className="truncate text-xs text-muted-foreground">{g.course || "—"}</p>
              </div>
              <span className="shrink-0 font-display text-xl font-bold text-primary">{g.grade}/{g.max}</span>
            </div>
            {g.feedback && <p className="mt-2 border-t border-border pt-2 text-sm text-muted-foreground">{g.feedback}</p>}
          </div>
        ))}
      </div>

      {/* Vue tableau (desktop) */}
      <div className="hidden rounded-xl border border-border bg-card md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Intitulé</TableHead>
              <TableHead>Cours</TableHead>
              <TableHead className="text-right">Note</TableHead>
              <TableHead>Commentaire</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {grades.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">Aucune note pour l'instant.</TableCell></TableRow>
            ) : grades.map((g, i) => (
              <TableRow key={i}>
                <TableCell><Badge variant="secondary">{g.type}</Badge></TableCell>
                <TableCell className="font-medium">{g.title}</TableCell>
                <TableCell className="text-muted-foreground">{g.course || "—"}</TableCell>
                <TableCell className="text-right font-display font-bold text-primary">{g.grade}/{g.max}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{g.feedback || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
